import React from 'react';
import { useTranslation } from 'react-i18next';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { TextArea } from '@/components/ui/inputs/TextArea';
import {
  useCostCenterFormPanel,
  type CostCenterFormRecord,
} from '@/hooks/organization/costCenter/useCostCenterFormPanel';


interface CostCenterFormPanelProps {
  onSubmit?: () => void;
  readOnly?: boolean;
  record?: CostCenterFormRecord;
}

export const CostCenterFormPanel: React.FC<CostCenterFormPanelProps> = ({
  onSubmit,
  readOnly,
  record,
}) => {
  const { t: translate } = useTranslation();

  const {
    codeValue,
    nameValue,
    descriptionValue,
    metadata,
    isEditMode,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    canSave,
    handleCodeChange,
    handleNameChange,
    handleDescriptionChange,
    handleSubmit,
    errors,
    isSubmitted,
  } = useCostCenterFormPanel({ onSubmit, readOnly, record });

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="cost-center-form-panel">
      <SidePanelFormLayout.Fields>
        {isEditMode && metadata && (
          <LabelInput
            label={translate('organization:fields.id')}
            value={metadata.id}
            disabled
          />
        )}

        <LabelInput
          label={translate('organization:fields.costCenterName')}
          value={nameValue}
          onChange={handleNameChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.name && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.name?.message && isSubmitted
              ? translate(String(errors.name.message))
              : undefined
          }
          data-testid="cost-center-name-input"
        />

        <LabelInput
          label={translate('organization:fields.costCenterCode')}
          value={codeValue}
          onChange={handleCodeChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.code && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.code?.message && isSubmitted
              ? translate(String(errors.code.message))
              : undefined
          }
          data-testid="cost-center-code-input"
        />

        <TextArea
          label={translate('organization:fields.description')}
          value={descriptionValue}
          onChange={handleDescriptionChange}
          required
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.description && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors.description?.message && isSubmitted
              ? translate(String(errors.description.message))
              : undefined
          }
          maxLength={8000}
          rows={5}
          showCharacterCount
          data-testid="cost-center-description-input"
        />

        {isEditMode && metadata && (
          <>
            <LabelInput label={translate('common:fields.createdDate')} value={metadata.createdDate} disabled />
            <LabelInput label={translate('common:fields.createdBy')} value={metadata.createdBy} disabled />
            <LabelInput label={translate('common:fields.modifiedDate')} value={metadata.modifiedDate} disabled />
            <LabelInput label={translate('common:fields.modifiedBy')} value={metadata.modifiedBy} disabled />
          </>
        )}
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action={isEditMode ? 'edit' : 'create'} denied={!canSave}>
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={!isReady || isSubmitting}
            >
              {translate(isEditMode ? 'common:actions.save' : 'organization:actions.registerCostCenter')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default CostCenterFormPanel;
